/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import type Store from "@ember-data/store";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import CompoundSurveyFrequency from "parog-web/models/compound-survey-frequency";

import { Changeset } from "ember-changeset";
import type { BufferedChangeset } from "ember-changeset/types";
import type IntlService from "ember-intl/services/intl";
import ForcBaseComponent from "parog-web/components/forc/abstract/base-component";
import type ConfigurationVitalSignModel from "parog-web/models/configuration-vital-sign";
import type Patient from "parog-web/models/patient";
import VitalSignItem from "parog-web/models/vital-sign/vital-sign-item";
import type ConfigurationService from "parog-web/services/configuration";
import { Configuration } from "parog-web/services/configuration";

interface ConstantItem {
  enabled: boolean;
  frequency?: string;
  forgettingNumber?: number;
  label?: string;
}
class ConstantMonitoringModelView {
  constructor(
    private store: Store,
    private patient: Patient,
    private configurationVitalSign: ConfigurationVitalSignModel,
    private surveyFrequencies: CompoundSurveyFrequency[]
  ) {}

  async save(): Promise<void> {
    // Create a new CompoundSurveyFrequenciesChangedJe that contains only enabled frequencies
    const freqChangedJeReq = this.store
      .createRecord("compound-survey-frequencies-changed-journal-event", {
        patient: this.patient,
        occurredAt: new Date(),
        source: "wise",
        enabledSurveyFrequencies: this.surveyFrequencies.filter(f => f.enabled)
      })
      .save();

    await Promise.all([this.configurationVitalSign.save(), freqChangedJeReq]);
  }

  get filteredVitalSigns(): VitalSignItem[] {
    const followUpReasonIdsToFilter = ["digest-rch", "digest-crohn"];
    if (followUpReasonIdsToFilter.includes(this.patient.followUpReasonId)) {
      if (this.patient.followUpReasonId === "digest-rch") {
        return this.configurationVitalSign.vitalSigns.filter(
          v => v.id !== "crohn-survey"
        );
      }
      if (this.patient.followUpReasonId === "digest-crohn") {
        return this.configurationVitalSign.vitalSigns.filter(
          v => v.id !== "rch-survey"
        );
      }
    }
    return this.configurationVitalSign.vitalSigns;
  }

  get constants(): ConstantItem[] {
    return [...this.filteredVitalSigns, ...this.surveyFrequencies];
  }

  get enabledConstants(): ConstantItem[] {
    return this.constants.filter(c => c.enabled);
  }

  @tracked
  isCustomDirty = false;
}

interface ConstantMonitoringChangeset extends BufferedChangeset {
  enabledConstants: ConstantItem[];
}

export default class ConstantMonitoringEditionComponent extends ForcBaseComponent {
  @service declare configuration: ConfigurationService;
  @service declare intl: IntlService;
  @service declare store: Store;

  static fullComponentId = "constant-monitoring/edition-form";

  private declare _changeset: ConstantMonitoringChangeset;

  get changeset(): ConstantMonitoringChangeset {
    if (this._changeset) return this._changeset;

    const configuration = this.configuration.configurationForPatient(
      Configuration.VitalSign,
      this.patient
    );

    const compoundSurveyFrequencies = this.patient.surveyFrequencies;

    this._changeset = Changeset(
      new ConstantMonitoringModelView(
        this.store,
        this.patient,
        configuration,
        compoundSurveyFrequencies
      )
    ) as ConstantMonitoringChangeset;

    return this._changeset;
  }

  get shouldShowFrequency(): boolean {
    return (
      this.patient.tlsAgreed &&
      this.changeset.enabledConstants.some(constant =>
        Boolean(constant.frequency)
      )
    );
  }

  get shouldShowForgetting(): boolean {
    return (
      this.patient.tlsAgreed &&
      this.changeset.enabledConstants.some(
        constant => constant.forgettingNumber != undefined
      )
    );
  }

  get isDirty() {
    return this.changeset.isCustomDirty as boolean;
  }

  async save(): Promise<void> {
    return await this.changeset.save();
  }

  labelFromConstant = (
    constant: VitalSignItem | CompoundSurveyFrequency
  ): string => {
    if (constant instanceof VitalSignItem) {
      return this.getVitalSignLabel(constant);
    } else if (constant instanceof CompoundSurveyFrequency) {
      return this.getCompoundSurveyLabel(constant);
    }
    return "";
  };

  private getVitalSignLabel(item: VitalSignItem): string {
    return this.intl.t(`vital-sign.${item.id}.long-description`);
  }

  private getCompoundSurveyLabel(item: CompoundSurveyFrequency): string {
    const survey = this.store.peekRecord("compound-survey", item.surveyId);
    if (!survey)
      throw `No corresponding compound-survey found for the id '${item.surveyId}'`;
    return survey.title;
  }

  @action
  setDirtiness() {
    this.changeset.set("isCustomDirty", true);
  }
}
